import { useState, useEffect } from 'react';
import type { UsbDeviceDebugInfo } from '../../../../types/electron/usb-device.types';
import type { UsbDeviceLogEntry } from '../../../../hooks/useUsbDevice';

interface UsbDeviceDebugPanelProps {
  isSupported: boolean;
  isMagicLeapConnected: boolean;
  eventLog: UsbDeviceLogEntry[];
  getDebugInfo: () => Promise<UsbDeviceDebugInfo | null>;
}

export function UsbDeviceDebugPanel({
  isSupported,
  isMagicLeapConnected,
  eventLog,
  getDebugInfo,
}: UsbDeviceDebugPanelProps) {
  const [debugInfo, setDebugInfo] = useState<UsbDeviceDebugInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [simulateError, setSimulateError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const info = await getDebugInfo();
      setDebugInfo(info);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Simula conexión/desconexión de las Magic Leap 2 sin hardware real.
   * El evento entra por el mismo pipeline que el hotplug físico.
   */
  const simulate = async (type: 'connected' | 'disconnected') => {
    const api = window.electron?.usbDevice;
    if (!api?.simulate) {
      setSimulateError('API de simulación no disponible en este entorno');
      return;
    }

    setSimulating(true);
    setSimulateError(null);
    try {
      const result = await api.simulate(type, 'magic-leap-2');
      if (!result.applied) setSimulateError(result.reason ?? 'Sin cambios');
      await refresh();
    } catch (err) {
      setSimulateError(err instanceof Error ? err.message : String(err));
    } finally {
      setSimulating(false);
    }
  };

  useEffect(() => {
    if (isSupported) refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupported]);

  const panelStyle: React.CSSProperties = {
    position: 'fixed',
    top: 16,
    right: 16,
    zIndex: 999999999,
    background: 'rgba(10,20,40,0.96)',
    color: '#e2e8f0',
    borderRadius: 10,
    padding: '14px 18px',
    minWidth: 340,
    maxWidth: 420,
    maxHeight: '70vh',
    overflowY: 'auto',
    fontFamily: 'monospace',
    fontSize: 12,
    boxShadow: '0 4px 32px rgba(0,0,0,0.5)',
    border: '1px solid rgba(99,102,241,0.4)',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  };

  const badge = (ok: boolean, yes = 'SÍ', no = 'NO') => (
    <span style={{ color: ok ? '#4ade80' : '#f87171', fontWeight: 700 }}>{ok ? yes : no}</span>
  );

  const vidpid = (v: number, p?: number) =>
    `0x${v.toString(16).toUpperCase().padStart(4, '0')} / ${
      p === undefined ? 'cualquiera' : `0x${p.toString(16).toUpperCase().padStart(4, '0')}`
    }`;

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center' }}>
        <span style={{ color: '#a5b4fc', fontWeight: 700, fontSize: 13 }}>
          <i className="las la-usb" style={{ marginRight: 6 }} />
          USB Device Debug
        </span>
        <button
          onClick={refresh}
          disabled={loading}
          style={{ background: '#3b4fe4', border: 'none', color: '#fff', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 11 }}
        >
          <i className="las la-sync" style={{ marginRight: 4 }} />
          {loading ? '...' : 'refresh'}
        </button>
      </div>

      {/* Estado básico */}
      <div style={{ marginBottom: 10 }}>
        <div style={rowStyle}><span>Electron API usbDevice</span>{badge(isSupported)}</div>
        <div style={rowStyle}><span>WebUSB disponible</span>{badge(!!debugInfo?.webUsbAvailable)}</div>
        <div style={rowStyle}><span>Magic Leap 2 conectada</span>{badge(isMagicLeapConnected)}</div>
      </div>

      {/* Simulador — permite probar el flujo sin las gafas físicas */}
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4, marginTop: 8 }}>SIMULADOR MAGIC LEAP 2</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => simulate('connected')}
          disabled={simulating || isMagicLeapConnected}
          style={{
            flex: 1,
            background: isMagicLeapConnected ? '#334155' : '#16a34a',
            border: 'none',
            color: '#fff',
            borderRadius: 4,
            padding: '5px 8px',
            cursor: simulating || isMagicLeapConnected ? 'default' : 'pointer',
            fontSize: 11,
          }}
        >
          <i className="las la-plug" style={{ marginRight: 4 }} />
          conectar
        </button>
        <button
          onClick={() => simulate('disconnected')}
          disabled={simulating || !isMagicLeapConnected}
          style={{
            flex: 1,
            background: !isMagicLeapConnected ? '#334155' : '#dc2626',
            border: 'none',
            color: '#fff',
            borderRadius: 4,
            padding: '5px 8px',
            cursor: simulating || !isMagicLeapConnected ? 'default' : 'pointer',
            fontSize: 11,
          }}
        >
          <i className="las la-times-circle" style={{ marginRight: 4 }} />
          desconectar
        </button>
      </div>
      {simulateError && (
        <div style={{ color: '#fbbf24', fontSize: 11, marginTop: 4 }}>{simulateError}</div>
      )}

      {debugInfo && (
        <>
          {/* Dispositivos monitorizados */}
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4, marginTop: 8 }}>MONITORIZADOS ({debugInfo.monitoredDevices.length})</div>
          {debugInfo.monitoredDevices.map((d: UsbDeviceDebugInfo['monitoredDevices'][number], i: number) => (
            <div key={i} style={{ ...rowStyle, color: '#cbd5e1' }}>
              <span>{d.productName}</span>
              <span style={{ color: '#64748b' }}>{vidpid(d.vendorId, d.productId)}</span>
            </div>
          ))}

          {/* Dispositivos autorizados por WebUSB */}
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4, marginTop: 8 }}>AUTORIZADOS WebUSB ({debugInfo.authorizedDevices.length})</div>
          {debugInfo.authorizedDevices.length === 0
            ? <div style={{ color: '#64748b', fontSize: 11 }}>ninguno</div>
            : debugInfo.authorizedDevices.map((d: UsbDeviceDebugInfo['authorizedDevices'][number], i: number) => (
              <div key={i} style={{ ...rowStyle, color: '#cbd5e1' }}>
                <span>{d.productName ?? '-'}</span>
                <span style={{ color: '#64748b' }}>{d.vendorId}/{d.productId}</span>
              </div>
            ))}

          {/* Dispositivos conectados según el manager */}
          <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4, marginTop: 8 }}>CONECTADOS main process ({debugInfo.connectedDevices.length})</div>
          {debugInfo.connectedDevices.length === 0
            ? <div style={{ color: '#64748b', fontSize: 11 }}>ninguno</div>
            : debugInfo.connectedDevices.map((d: UsbDeviceDebugInfo['connectedDevices'][number], i: number) => (
              <div key={i} style={{ ...rowStyle, color: '#4ade80' }}>
                <span>
                  {d.productName}
                  {d.simulated && (
                    <span style={{ color: '#fbbf24', marginLeft: 6, fontSize: 10 }}>SIMULADO</span>
                  )}
                </span>
                <span>{d.serialNumber ?? '-'}</span>
              </div>
            ))}
        </>
      )}

      {/* Log de eventos */}
      <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 4, marginTop: 10 }}>EVENTOS ({eventLog.length})</div>
      {eventLog.length === 0
        ? <div style={{ color: '#64748b', fontSize: 11 }}>sin eventos aún</div>
        : eventLog.map((e, i) => (
          <div key={i} style={{ ...rowStyle, color: e.type === 'connected' ? '#4ade80' : '#f87171' }}>
            <span>
              <i className={e.type === 'connected' ? 'las la-arrow-up' : 'las la-arrow-down'} style={{ marginRight: 4 }} />
              {e.deviceName}
            </span>
            <span style={{ color: '#64748b' }}>{new Date(e.timestamp).toLocaleTimeString()}</span>
          </div>
        ))}

      {!isSupported && (
        <div style={{ marginTop: 10, color: '#fbbf24', fontSize: 11 }}>
          <i className="las la-exclamation-triangle" style={{ marginRight: 4 }} />
          window.electron.usbDevice no disponible.<br />
          El Electron instalado no tiene esta versión del código.<br />
          Necesita rebuild + reinstalar.
        </div>
      )}
    </div>
  );
}
